import {
  Attribute,
  BelongsTo,
  JsonApiModel,
  JsonApiModelConfig,
} from '@elearnio/angular2-jsonapi';

import { Course } from '@models/course.model';
import { ILTCourse } from '@models/ilt-course.model';
import { LearningPath } from '@models/learning-path.model';

// Translatable text fields carried in the `fields` object. Cover is a separate attribute.
//
// `fields` is a sparse jsonb map passed through verbatim by the backend resource (no per-key
// whitelist), so the content page (page 2) extends it with section-scoped keys without any
// backend change:
//  - `sectionTitles`: per-section-id translated section header ("Titel") — one entry per section.
//  - `moduleLabels`: per-activity-id translated standalone label text (the "Textfeld" items).
// The profile page keeps writing `name`/`description`; because both pages seed their draft from the
// full persisted record and write every group back, neither page clobbers the other's keys.
export interface TrainingTranslationFields {
  name?: string;
  description?: string;
  sectionTitles?: Record<string, string>;
  moduleLabels?: Record<string, string>;
}

@JsonApiModelConfig({
  type: 'training_translations',
})
export class TrainingTranslation extends JsonApiModel {
  @Attribute()
  language!: string;

  @Attribute()
  fields!: TrainingTranslationFields;

  @Attribute({ serializedName: 'training_id' })
  trainingId!: string;

  @Attribute({ serializedName: 'training_type' })
  trainingType!: string;

  // READ = URL string; WRITE = base64 data-URI.
  @Attribute({ serializedName: 'cover_image' })
  coverImage!: string | null;

  // Per-language certificate template override (settings page); null = use the
  // training's own source-language template. A real FK, not part of `fields`.
  @Attribute({ serializedName: 'certificate_template_id' })
  certificateTemplateId!: number | null;

  // "Visible to users" toggle (Manage-languages modal): false = the language is
  // hidden from learners (author-editable draft); true = visible. Default-safe:
  // older payloads without the attribute (undefined at runtime) count as active
  // — always read through `active !== false`. The source language has no
  // TrainingTranslation row and is always active.
  @Attribute()
  active!: boolean | null;

  @Attribute({ serializedName: 'created_at' })
  createdAt!: Date;

  @Attribute({ serializedName: 'updated_at' })
  updatedAt!: Date;

  // Polymorphic parent — the canonical way to set the training on create (mirrors PublishingApproval).
  @BelongsTo({ key: 'training' })
  training!: Course | ILTCourse | LearningPath;
}
